import { Link, useParams } from 'react-router-dom';
import { SEOHead } from '@/components/layout/SEOHead';
import { ProjectScrollNarrative } from '@/components/gallery/ProjectScrollNarrative';
import { GalleryCard } from '@/components/gallery/GalleryCard';
import { FadeIn } from '@/components/motion/FadeIn';
import { getProject, getAllProjects, getContact } from '@/lib/content';
import { buildProjectSchema } from '@/lib/seo';

export function ProjectDetailPage() {
  const { slug } = useParams();
  const project = slug ? getProject(slug) : null;
  const contact = getContact();
  const all = getAllProjects();

  if (!project) {
    return (
      <>
        <SEOHead title="Project not found · Brian Ellison Design" />
        <section className="container section">
          <p className="eyebrow">404</p>
          <h1>This project couldn't be found.</h1>
          <p><Link to="/projects" className="text-link">View all projects</Link></p>
        </section>
      </>
    );
  }

  const idx = all.findIndex((p) => p.slug === project.slug);
  const next = all[(idx + 1) % all.length];
  const schema = buildProjectSchema(project, contact);

  // Related projects — prefer same typology, fall back to chronological neighbors
  const related = all
    .filter((p) => p.slug !== project.slug)
    .sort((a, b) => {
      const aSame = a.typology === project.typology ? 0 : 1;
      const bSame = b.typology === project.typology ? 0 : 1;
      if (aSame !== bSame) return aSame - bSame;
      return Math.abs(a.year - project.year) - Math.abs(b.year - project.year);
    })
    .slice(0, 3);

  return (
    <>
      <SEOHead
        title={`${project.title} · ${project.typology} · Brian Ellison Design`}
        description={project.summary}
        canonical={`https://brianellisondesign.com/projects/${project.slug}`}
        ogImage={`/og/projects/${project.slug}.svg`}
        schema={schema}
      />
      <ProjectScrollNarrative project={project} />

      <section className="project-related section">
        <div className="container">
          <FadeIn>
            <p className="eyebrow">Related work</p>
            <h2 className="project-related__heading">More projects.</h2>
          </FadeIn>
          <div className="project-related__grid">
            {related.map((p, i) => (
              <FadeIn key={p.slug} delay={i * 80}>
                <GalleryCard project={p} index={i} />
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {next ? (
        <section className="project-next container section">
          <p className="eyebrow">Continue</p>
          <Link to={`/projects/${next.slug}`} className="project-next__link">
            <span className="project-next__chevron" aria-hidden="true">→</span>
            <span className="display-italic project-next__title">{next.title}</span>
          </Link>
        </section>
      ) : null}
    </>
  );
}
